import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { PublicationItemContent } from '@/src/features/publication/ui/components/PublicationItemContent'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { PUBLICATION_TRACKING_SOURCE, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import type { TGetCommunityApiData } from '../../../api/getCommunity.types'
import { usePublicationsOfCommunityQuery } from '../../../services/usePublicationsOfCommunityQuery'
import { useTodayPublication } from '../../hooks/useTodayPublication'
import { itemClassName, PublicationPlaceholders } from '../CommunityScreen/PublicationsPlaceholders'

type TVirtusTodayPublicationProps = {
  community: TGetCommunityApiData
}

/**
 * Virtus journeys are followed live: the publication to pray is always today's one,
 * not the oldest unprayed one like on a classic journey. A publication is only shown
 * when its publication date is today.
 */
export const VirtusTodayPublication = ({ community }: TVirtusTodayPublicationProps): ReactNode => {
  const router = useRouter()
  const { routes } = useRoutes()

  const hasStarted = !!community.fromDate && !dayjs().isBefore(community.fromDate, 'day')
  const publications = usePublicationsOfCommunityQuery({
    id: community.id,
    type: community?.lifetime,
    enabled: hasStarted,
  })

  const { todayPublication, isSearching } = useTodayPublication(publications)

  if (hasStarted && isSearching) return <PublicationPlaceholders nb={1} />

  if (!hasStarted || !todayPublication)
    return (
      <View className="gap-3 pt-6" testID={TEST_ID.COMMUNITY.VIRTUS_TODAY_PUBLICATION}>
        <View className={`${itemClassName} flex-col gap-2 items-center justify-between`}>
          <Icon name={ICON.BOOK_OPEN} size="xl" className="text-default-icon-contrast-lower" />
          <Typography size="xl" className="text-default-icon-contrast-lower">
            <FormattedMessage id="community.virtus.publications.empty" />
          </Typography>
        </View>
      </View>
    )

  return (
    <View className="gap-3 pt-6" testID={TEST_ID.COMMUNITY.VIRTUS_TODAY_PUBLICATION}>
      <View className={`${itemClassName} flex-row items-center justify-between`}>
        <Typography bold size="lg">
          <FormattedMessage id="word.today" />
        </Typography>
        <Button
          variant="faded"
          colors="default"
          size="sm"
          endIcon={ICON.CHEVRON_RIGHT}
          onPress={() => router.push(routes.communityPublications({ id: community.id }))}
        >
          <FormattedMessage id="words.see-all.sing" />
        </Button>
      </View>
      <View className={itemClassName}>
        <PublicationItemContent
          publication={{ ...todayPublication, community }}
          trackingSource={TRACKING_SOURCE.COMMUNITY_PAGE}
          publicationTrackingSource={PUBLICATION_TRACKING_SOURCE.COMMUNITY_READ_MORE}
        />
      </View>
    </View>
  )
}
